<template>
    <!-- Prestation de bordereau insérée dans un lot : quantité de la prestation et quantité de chaque ligne pour 1 unité -->
    <b-modal :id="modalId" size="xl" dialog-class="ix-service-modal" footer-class="ix-service-modal-footer">
        <template #modal-title>
            Modifier la prestation<span v-if="name"> – {{ name }}</span>
        </template>

        <template v-if="header">
            <div class="ix-service-modal-quantity">
                <div>
                    <label class="required" :for="modalId + '-quantity'">Quantité de la prestation</label>
                    <div class="input-group">
                        <input type="number" step="0.001" min="0" class="form-control text-right" :class="{'is-invalid': !quantityValid}" :id="modalId + '-quantity'" v-model="quantity" @keydown.enter.prevent>
                        <input type="text" class="form-control ix-service-modal-unit" v-model="unitInput" maxlength="50" placeholder="Unité" aria-label="Unité de la prestation" @keydown.enter.prevent>
                    </div>
                    <small class="ix-service-modal-help">
                        Qté totale = qté pour 1{{ unit ? ' ' + unit : '' }} × quantité de la prestation. Les lignes au forfait gardent une quantité fixe.
                    </small>
                    <!-- Report dans la synthèse du lot (ou la synthèse de l'étude sans lot) -->
                    <div class="custom-control custom-switch ix-service-modal-inject">
                        <input type="checkbox" class="custom-control-input" :id="modalId + '-inject'" v-model="injectInContext">
                        <label class="custom-control-label" :for="modalId + '-inject'">Reporter la quantité et l'unité {{ contextLabel }}</label>
                    </div>
                </div>
            </div>

            <div class="ix-table-box mb-0">
                <table class="ix-table ix-service-modal-table">
                    <thead>
                        <tr>
                            <th style="width: 120px;">Catégorie</th>
                            <th>Désignation</th>
                            <th class="text-center" style="width: 70px;">Forfait</th>
                            <th class="text-right" style="width: 120px;">Qté pour 1{{ unit ? ' ' + unit : '' }}</th>
                            <th class="text-right" style="width: 130px;">Qté totale</th>
                            <th style="width: 70px;">Unité</th>
                            <th class="text-right" style="width: 140px;">Prix de revient HT</th>
                            <th class="text-right" style="width: 130px;">Total vente HT</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in rows" :key="row.line.uniq">
                            <td>{{ typeLabel(row.line.type) }}</td>
                            <td>
                                {{ row.line.designation || 'Ligne sans désignation' }}
                                <span class="badge badge-secondary ml-1" v-if="row.forfait">Forfait</span>
                            </td>
                            <td class="text-center">
                                <input type="checkbox" v-model="row.forfait" @change="onForfaitChange(row)" :title="row.forfait ? 'Quantité fixe' : 'Quantité proportionnelle à la prestation'">
                            </td>
                            <td class="text-right">
                                <span class="ix-text-secondary" v-if="row.forfait">—</span>
                                <input v-else type="number" step="0.000001" min="0" class="form-control form-control-sm text-right" :class="{'is-invalid': !isNumber(row.unitQuantity)}" v-model="row.unitQuantity" @keydown.enter.prevent>
                            </td>
                            <td class="text-right">
                                <input v-if="row.forfait" type="number" step="0.001" min="0" class="form-control form-control-sm text-right" :class="{'is-invalid': !isNumber(row.fixedQuantity)}" v-model="row.fixedQuantity" @keydown.enter.prevent>
                                <template v-else>
                                    <input type="text" class="form-control form-control-sm text-right ix-service-modal-computed" :value="formatQuantity(totalQuantity(row))" readonly tabindex="-1">
                                    <small class="ix-service-block-formula">{{ formula(row) }}</small>
                                </template>
                            </td>
                            <td>{{ lineUnit(row) }}</td>
                            <td class="text-right ix-nowrap">{{ currency(row.line.amount) }}<span v-if="perHour(row.line)"> / h</span></td>
                            <td class="text-right ix-nowrap">{{ currency(lineSelling(row)) }}</td>
                        </tr>
                        <tr v-if="rows.length === 0">
                            <td colspan="8" class="text-center ix-text-secondary">Aucune ligne liée à cette prestation dans le lot.</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <template #modal-footer="{ cancel }">
            <div class="ix-service-modal-total">
                Total vente HT <strong>{{ currency(totalSelling) }}</strong>
                <span class="ix-text-secondary" v-if="quantityValid">soit {{ currency(totalSelling / parseFloat(quantity)) }}{{ unit ? ' / ' + unit : '' }}</span>
            </div>
            <div>
                <button type="button" class="ix-btn transparent" @click="cancel()">Annuler</button>
                <button type="button" class="ix-btn green" :disabled="!isValid" @click="apply">Appliquer au lot</button>
            </div>
        </template>
    </b-modal>
</template>

<script>
export default {
    name: 'service-block-modal',
    props: {
        // Catégories des prévisions [{value, label}]
        types: { type: Array, default: () => [] },
    },
    data() {
        return {
            modalId: 'service-block-modal-' + Math.random().toString(36).substr(2, 9),
            header: null,
            lotUnit: '',
            contextLabel: 'dans la synthèse du lot',
            unitInput: '',
            injectInContext: false,
            quantity: '',
            rows: [],
        };
    },
    computed: {
        name() {
            return this.header ? String(this.header.designation || '').replace(/\s*\(x[^)]*\)\s*$/, '') : '';
        },
        unit() {
            return String(this.unitInput || '').trim();
        },
        quantityValid() {
            return this.isNumber(this.quantity) && parseFloat(this.quantity) > 0;
        },
        isValid() {
            return this.quantityValid && this.rows.every(row => row.forfait ? this.isNumber(row.fixedQuantity) : this.isNumber(row.unitQuantity));
        },
        totalSelling() {
            return this.rows.reduce((sum, row) => sum + this.lineSelling(row), 0);
        },
    },
    methods: {
        // header : ligne d'en-tête de la prestation ; lines : ses lignes dans le lot
        // lotUnit : unité du lot ou de l'étude ; contextLabel : « dans la synthèse du lot » / « dans la synthèse de l'étude »
        open(header, lines, lotUnit, contextLabel) {
            this.header = header;
            this.lotUnit = lotUnit ? String(lotUnit).trim() : '';
            this.contextLabel = contextLabel || 'dans la synthèse du lot';
            // Unité de la prestation si elle existe, sinon celle du lot ou de l'étude
            this.unitInput = header.service_unit || this.lotUnit;
            this.injectInContext = false;
            this.quantity = header.service_quantity !== null && header.service_quantity !== undefined ? String(parseFloat(header.service_quantity)) : '';
            this.rows = lines.map(line => {
                var forfait = line.unit === 'forfait';
                var unitQuantity = line.service_unit_quantity;
                // Lien sans quantité unitaire : déduite de la quantité actuelle
                if (!this.isNumber(unitQuantity) && this.isNumber(line.quantity) && parseFloat(header.service_quantity) > 0) {
                    unitQuantity = parseFloat(line.quantity) / parseFloat(header.service_quantity);
                }

                return {
                    line: line,
                    forfait: forfait,
                    // Unité d'origine, rétablie si la ligne n'est plus au forfait
                    unit: forfait ? '' : (line.unit || ''),
                    unitQuantity: String(this.round(unitQuantity, 6)),
                    fixedQuantity: String(parseFloat(line.quantity) || 1),
                };
            });
            this.$bvModal.show(this.modalId);
        },
        onForfaitChange(row) {
            if (row.forfait) {
                row.fixedQuantity = String(this.round(this.totalQuantityProportional(row), 3) || 1);
            } else if (!this.isNumber(row.unitQuantity) && this.quantityValid) {
                row.unitQuantity = String(this.round(parseFloat(row.fixedQuantity) / parseFloat(this.quantity), 6));
            }
        },
        totalQuantityProportional(row) {
            return this.quantityValid && this.isNumber(row.unitQuantity) ? this.round(parseFloat(row.unitQuantity) * parseFloat(this.quantity), 3) : 0;
        },
        totalQuantity(row) {
            return row.forfait ? (parseFloat(row.fixedQuantity) || 0) : this.totalQuantityProportional(row);
        },
        lineSelling(row) {
            return this.totalQuantity(row) * (parseFloat(row.line.selling) || 0);
        },
        formula(row) {
            if (!this.isNumber(row.unitQuantity) || !this.quantityValid) {
                return '';
            }

            return this.formatQuantity(row.unitQuantity, 6) + ' × ' + this.formatQuantity(this.quantity) + (this.unit ? ' ' + this.unit : '');
        },
        apply() {
            if (!this.isValid) {
                return;
            }

            this.$emit('apply', {
                header: this.header,
                quantity: parseFloat(this.quantity),
                unit: this.unit,
                injectInContext: this.injectInContext,
                lines: this.rows.map(row => ({
                    line: row.line,
                    forfait: row.forfait,
                    unit: row.unit,
                    unitQuantity: row.forfait ? null : parseFloat(row.unitQuantity),
                    quantity: this.totalQuantity(row),
                })),
            });
            this.$bvModal.hide(this.modalId);
        },
        typeLabel(type) {
            var found = this.types.find(item => item.value == type);

            return found ? found.label : '';
        },
        perHour(line) {
            return line.type == 1 || line.type == 4;
        },
        lineUnit(row) {
            if (row.forfait) {
                return 'forfait';
            }

            return this.perHour(row.line) ? 'h' : row.unit;
        },
        isNumber(value) {
            return value !== null && value !== undefined && value !== '' && !isNaN(parseFloat(value)) && isFinite(value);
        },
        round(value, decimals) {
            var factor = Math.pow(10, decimals);

            return Math.round((parseFloat(value) || 0) * factor) / factor;
        },
        formatQuantity(value, decimals) {
            return (parseFloat(value) || 0).toLocaleString('fr-FR', {maximumFractionDigits: decimals || 3});
        },
    },
};
</script>

<style>
    .ix-service-modal-quantity {
        display: flex;
        align-items: flex-start;
        gap: 20px;
        margin-bottom: 16px;
    }

    .ix-service-modal-quantity label {
        display: block;
        margin-bottom: 4px;
        font-size: 13px;
    }

    .ix-service-modal-quantity .input-group {
        width: 260px;
    }

    .ix-service-modal-unit {
        flex: 0 0 90px !important;
        max-width: 90px;
    }

    .ix-service-modal-inject {
        margin-top: 8px;
        white-space: nowrap;
    }

    .ix-service-modal-help {
        display: block;
        margin-top: 4px;
        font-size: 12px;
        color: var(--ix-text-secondary);
    }

    .ix-service-modal-table td {
        vertical-align: middle;
    }

    .ix-service-modal-table .ix-service-modal-computed {
        background: #EEF3FA;
    }

    .ix-service-modal-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background: var(--ix-background-secondary);
    }

    .ix-service-modal-footer > div:last-child {
        display: flex;
        gap: 8px;
    }

    .ix-service-modal-total strong {
        font-size: 1.15em;
        margin: 0 4px;
    }
</style>
